/**
 * Coalesces many events into occasional `router.refresh()` calls.
 * Debounce waits for a burst to settle; minInterval caps how often refresh runs.
 */
export function createRefreshScheduler(
	refresh: () => void,
	opts: { debounceMs: number; minIntervalMs: number },
) {
	let debounceTimer: ReturnType<typeof setTimeout> | undefined;
	let tailTimer: ReturnType<typeof setTimeout> | undefined;
	let lastRun = 0;

	function runRefresh() {
		const now = Date.now();
		if (now - lastRun >= opts.minIntervalMs) {
			lastRun = now;
			refresh();
			return;
		}
		const wait = opts.minIntervalMs - (now - lastRun);
		if (tailTimer) clearTimeout(tailTimer);
		tailTimer = setTimeout(() => {
			tailTimer = undefined;
			lastRun = Date.now();
			refresh();
		}, wait);
	}

	function schedule() {
		if (debounceTimer) clearTimeout(debounceTimer);
		debounceTimer = setTimeout(() => {
			debounceTimer = undefined;
			runRefresh();
		}, opts.debounceMs);
	}

	function cancel() {
		if (debounceTimer) clearTimeout(debounceTimer);
		if (tailTimer) clearTimeout(tailTimer);
		debounceTimer = undefined;
		tailTimer = undefined;
	}

	return { schedule, cancel };
}
